import { computed } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { LoginEvents, Screens, type ScreenChangeData } from '@common/auth-layer/types'
import { MIGRATION_GATE_PATHS_SET } from '@common/auth-layer/utils/migration'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { useRoute, useRouter } from 'nuxt/app'
import type { RouteName } from '@common/navigation-layer/config/manual-routes'

/**
 * Screen to route name mapping for auth pages
 */
const SCREEN_ROUTE_NAMES: Record<string, RouteName> = {
  [Screens.LOGIN]: 'auth-login',
  [Screens.LOGIN_WITH_EMAIL]: 'auth-login',
  [Screens.PROCEED_TO_LOGIN]: 'auth-login',
  [Screens.SIGN_UP]: 'auth-signup',
  [Screens.FORGOT_PASSWORD]: 'auth-forgot-password',
  [Screens.LOGIN_WITH_SECURE_CODE]: 'auth-login-with-code',
  [Screens.VERIFY_SECURE_CODE]: 'auth-login-with-code', // Same page handles verification
  [Screens.SET_PASSWORD]: 'auth-set-password', // Not navigated — SET_PASSWORD renders in-place (see createChangeScreenHandler guard)
  [Screens.EXPIRED_PASSWORD]: 'auth-expired-password',
  [Screens.PROFILE_CONFIRMATION]: 'auth-profile-confirmation',
}

/**
 * Get the route name for a given screen
 */
export const getScreenRouteName = (screen: string): RouteName => {
  return SCREEN_ROUTE_NAMES[screen] || 'auth-login'
}

/**
 * Composable for auth page shared logic
 * Handles redirects, navigation between auth screens, and login callbacks
 */
export const useAuthPage = () => {
  const route = useRoute()
  const router = useRouter()
  const { CPNavigateTo } = useCPNavigation()
  const auth = useAuth()

  // Get redirect URL from query params
  const redirectUrl = computed(() => {
    const redirect = route.query.redirect as string | undefined
    return redirect || null
  })

  // Get pre-filled email from query params
  const initialEmail = computed(() => {
    return (route.query.email as string) || ''
  })

  // Get referrer user from query params (for affiliate referrals)
  const referrerUser = computed(() => {
    const refCode = route.query.ref as string | undefined
    if (refCode && process.client) {
      window.localStorage.setItem('refCode', refCode)
    }
    return undefined
  })

  /**
   * Build query params preserving redirect and optionally adding email
   */
  const buildQueryParams = (email?: string): Record<string, string> | undefined => {
    const query: Record<string, string> = {}
    if (redirectUrl.value) {
      query.redirect = redirectUrl.value
    }
    if (email) {
      query.email = email
    }
    return Object.keys(query).length > 0 ? query : undefined
  }

  const handleLoginCallback = async (event?: { eventType?: string }) => {
    const currentPath = router.currentRoute.value.path
    const pathOnly = currentPath.split('?')[0]
    const isProfileConfirmationPath =
      pathOnly === '/profile-confirmation' || pathOnly.endsWith('/profile-confirmation')

    const isGateCleared =
      event?.eventType === LoginEvents.MIGRATION_GATE_CLEARED ||
      (event?.eventType === LoginEvents.LOGIN_SUCCESSFUL && isProfileConfirmationPath)

    // saveAuthAfterLogin has already navigated to a migration gate page.
    // Don't clobber that with a dashboard redirect.
    if (!isGateCleared && MIGRATION_GATE_PATHS_SET.has(pathOnly)) {
      return
    }

    if (route.query.redirect) {
      // For a normal login, saveAuthAfterLogin already triggered the
      // redirect. For a cleared migration gate, the gate intercepted the
      // original login's redirect, so we have to resume it here.
      if (isGateCleared) {
        await auth.handleRedirectAfterLogin()
      }
      return
    }

    await CPNavigateTo({ name: 'clientportal-dashboard' })
  }

  /**
   * Handle screen change - navigate to appropriate auth page
   * @param currentScreen - The current screen (to avoid unnecessary navigation)
   */
  const createChangeScreenHandler = (currentScreen: Screens) => {
    return async (data: ScreenChangeData) => {
      if (!data.page) return

      // For signup flow, OTP verification and back navigation should stay on signup page
      // Let AuthHandler manage the internal screen state without URL changes
      if (currentScreen === Screens.SIGN_UP) {
        if (data.page === Screens.VERIFY_SECURE_CODE || data.page === Screens.SIGN_UP) {
          // Stay on signup page, let AuthHandler handle screen changes internally
          return
        }
      }

      // PROCEED_TO_LOGIN is an intermediate screen shown after forgot-password success
      // Stay on current page so AuthHandler can display it; only navigate when user clicks (LOGIN)
      if (data.page === Screens.PROCEED_TO_LOGIN && currentScreen === Screens.FORGOT_PASSWORD) {
        return
      }

      // SET_PASSWORD is an in-place gate (password not yet set after OTP / Google
      // login). AuthHandler renders it without a route change — there is no
      // /set-password page file. Navigating would hit a 404 or the auth middleware
      // would bounce the user back to /login, losing all context.
      if (data.page === Screens.SET_PASSWORD) {
        return
      }

      const targetRouteName = getScreenRouteName(data.page)
      const currentRouteName = getScreenRouteName(currentScreen)

      // Don't navigate if already on the target route
      if (targetRouteName === currentRouteName) return

      // Navigate to the appropriate page with query params
      await CPNavigateTo({
        name: targetRouteName,
        query: buildQueryParams(data.email),
      })
    }
  }

  return {
    // Reactive state
    redirectUrl,
    initialEmail,
    referrerUser,

    // Handlers
    handleLoginCallback,
    createChangeScreenHandler,
    buildQueryParams,
  }
}
